import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePortalAuth } from "@/lib/contexts/PortalAuthContext";
import { usePartnerAccessTier } from "@/lib/hooks/usePartnerAccessTier";
import { supabase } from "@/src/integrations/supabase/client";

interface RequirePortalAuthProps {
  children: ReactNode;
}

// Routes a temp-tier (non-admin) user is allowed to visit. Everything else
// (including the full /portal/dashboard home and full-access pages) is
// redirected back to the Temp Portal home.
const TEMP_ALLOWED_PATHS = ["/portal/temp-preview"];
const CHANGE_PASSWORD_PATH = "/portal/change-password";

export default function RequirePortalAuth({
  children,
}: RequirePortalAuthProps) {
  const { user, loading } = usePortalAuth();
  const { tier, loading: tierLoading } = usePartnerAccessTier();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [mustChange, setMustChange] = useState<boolean | null>(null);
  const changePasswordMagicLink =
    location.pathname === CHANGE_PASSWORD_PATH &&
    new URLSearchParams(location.search).get("type") === "magiclink" &&
    !!new URLSearchParams(location.search).get("token_hash");

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setMustChange(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const [{ data: roleRows }, { data: profData }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", user.id),
        supabase
          .from("partner_profiles")
          .select("must_change_password")
          .eq("user_id", user.id)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      const roles = (roleRows ?? []).map((r: any) => r.role);
      // Treat any staff role (admin/editor/viewer) as "admin" for routing purposes.
      setIsAdmin(
        roles.some((r: string) => ["admin", "editor", "viewer"].includes(r)),
      );
      setMustChange(!!profData?.must_change_password);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (
    loading ||
    (user && tierLoading) ||
    (user && (isAdmin === null || mustChange === null))
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!user && changePasswordMagicLink) {
    return <>{children}</>;
  }

  if (!user) {
    return (
      <Navigate
        to="/portal/login"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    );
  }

  // Force password change before any other portal route (admins exempt).
  if (mustChange && !isAdmin && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  // Lock temp-tier (non-admin) users to the temp home page.
  if (
    tier === "temp" &&
    !isAdmin &&
    !mustChange &&
    !TEMP_ALLOWED_PATHS.includes(location.pathname)
  ) {
    return <Navigate to="/portal/temp-preview" replace />;
  }

  return <>{children}</>;
}
